/**
 * Full Video Player (wrapper around simplePlayer.tsx)
 * This component not only shows a video player but also handles some activities
 * (progress of the video, end of video, etc).
 * We use https://www.npmjs.com/package/player.js to handle the video player events.
 */
import React from 'react'
import { useBeforeUnload } from 'react-router-dom'
import playerjs from 'player.js'

import SimplePlayer from './simplePlayer'
import { useAnalytics, ANALYTICS_EVENTS } from '../../../libraries/posthog'

type Player = {
  on: (event: string, callback: (data: unknown) => void) => void,
  off: (event: string, callback?: (data: unknown) => void) => void,
}
type Props = {
  srcUrl: string, // url of the video to be played.
  playTime?: number, // number of seconds the video has been played, used to start the video at this time.
  handleVideoEnded: () => void // function to be called when the video ends.
  onClose: (playTime: number) => void, // to be called when the user closes/refreshes or navigates away from the page.
  videoId: number, // id of the video.
}

const FULL_PLAYER_ID = 'videoPlayer'
const playProgress: { [x: number] : { seconds: number } } = {}

const FullVideoPlayer: React.FC<Props> = ({ videoId, srcUrl, playTime = 0, handleVideoEnded, onClose }) => {
  const [player, setPlayer] = React.useState<Player | null>(null)
  const { trackEvent } = useAnalytics()
  React.useEffect(() => {
    return () => {
      onDestroyIframe()
    }
  }, [])

  React.useEffect(() => {
    if(player) {
      player.on('ready', () => {
        player.on('timeupdate', (time) => {
          playProgress[videoId] = time as { seconds: number }
        })
        player.on('ended', () => {
          playProgress[videoId] = { seconds: 0 }
          sendVideoActionEvents('video_ended')
          handleVideoEnded()
        })
        player.on('pause', () => {
          sendVideoActionEvents('video_paused')
        })
        player.on('play', () => {
          sendVideoActionEvents('video_played')
        })
      })
    }
    return () => {
      if (player) {
        player.off('ready')
      }
    }
  }, [player])

  useBeforeUnload(React.useCallback(() => {
    onDestroyIframe()
  }, []))

  const onLoadIframe = () => {
    const iframe = document.querySelector(`#${FULL_PLAYER_ID}`)
    setPlayer(new playerjs.Player(iframe))
  }

  const onDestroyIframe = () => {
    if(playProgress
      && playProgress[videoId]?.seconds > 0
      && playTime !== playProgress[videoId].seconds) {
      onClose(playProgress[videoId].seconds)
    }
  }

  const sendVideoActionEvents = (action: string) => {
    trackEvent(ANALYTICS_EVENTS.VIDEO_ACTION, { action })
  }

  return (
    <SimplePlayer
      id={FULL_PLAYER_ID}
      srcUrl={`${srcUrl}?t=${Math.floor(playTime)}&autoplay=false`}
      onLoad={onLoadIframe}
    />
  )
}

export default FullVideoPlayer
